import { RefObject, useCallback, useEffect, useRef, useState } from 'react'

import useFocus from '~/bootcamp/Activities/hooks/useFocus'

import {
  MultCoefficient2ActivityDataModel,
  generateMultCoefficient2ActivityData,
  checkMultCoefficient2Answer,
} from './MultCoefficient2.utils'

interface UseMultCoefficient2ReturnType {
  integers: number[]
  coefficient: number
  variable: string
  formValid: boolean
  answer: string
  handleCheckAnswer: () => void
  onInputChange: (val?: string) => void
  goToNextQuestion: () => void
  inputRef: RefObject<HTMLInputElement>
}

interface UseMultCoefficient2Props {
  difficultyLevel: number
  onNextQuestionClick: () => void
  onAnswerStateChange: (value: boolean | null) => void
  autofocus: boolean
  answerState: boolean | null
}

function UseMultCoefficient2({
  onAnswerStateChange,
  onNextQuestionClick,
  difficultyLevel,
  autofocus,
  answerState,
}: UseMultCoefficient2Props): UseMultCoefficient2ReturnType {
  const [taskHistory, setTaskHistory] = useState<string[]>([])
  const [activityData, setActivityData] = useState<MultCoefficient2ActivityDataModel>({
    answer: 0,
    integers: [],
    coefficient: 1,
    variable: '',
    equationString: '',
    maxHistoryLength: 3,
  })
  const [answer, setAnswer] = useState('')

  const inputRef = useRef<HTMLInputElement>(null)
  useFocus(autofocus, answerState, inputRef)

  useEffect(() => {
    setAnswer('')
    setActivityData(generateMultCoefficient2ActivityData(difficultyLevel, taskHistory))
  }, [taskHistory, difficultyLevel])

  const handleCheckAnswer = useCallback((): void => {
    onAnswerStateChange(
      checkMultCoefficient2Answer(activityData.answer, activityData.variable, activityData.equationString, answer),
    )
  }, [onAnswerStateChange, activityData.answer, activityData.variable, activityData.equationString, answer])

  const onInputChange = useCallback(
    (value: string) => {
      if (value === 'clear') {
        setAnswer('')
        onAnswerStateChange(null)
      } else {
        setAnswer(value)
      }
    },
    [onAnswerStateChange],
  )

  const goToNextQuestion = useCallback(() => {
    setTaskHistory((prevState) => [activityData.equationString, ...prevState].slice(0, activityData.maxHistoryLength))
    onNextQuestionClick()
  }, [onNextQuestionClick, activityData.equationString, activityData.maxHistoryLength])

  return {
    integers: activityData.integers,
    coefficient: activityData.coefficient,
    variable: activityData.variable,
    answer,
    formValid: answer !== '',
    handleCheckAnswer,
    onInputChange,
    goToNextQuestion,
    inputRef,
  }
}

export default UseMultCoefficient2
